import {
  dailyQuotaRuleChecker,
  DailyQuotaRuleStateType,
} from '../checkers/dailyQuotaRuleChecker';
import { getTimeNotificationsStore } from '../storage/stores';
import { display } from './index';
import { isProtectionEnabled } from '../protectionStatus';
import { isDelegationStarted } from '../delegation/delegation';
import { isSafeNetworkDelegated } from '../safeNetworks/safeNetworksDelegation';
import { t } from '../helper/locale';

const TIME_QUOTA_NOTIFICATION_ID = 'TIME_QUOTA_NOTIFICATION';

const ONE_MINUTE_IN_SECONDS = 60;
const FIVE_MINUTES_NOTIFICATION_RANGE = { min: 3, max: 5 };
const FIFTEEN_MINUTES_NOTIFICATION_RANGE = { min: 13, max: 15 };

/**
 * The extension owns quota notifications only in Delegation. Family devices
 * rely on KASA for the same alerts, and Safe Networks delegate protection away
 * from the extension.
 */
export const shouldShowTimeQuotaNotifications = () =>
  isProtectionEnabled() && isDelegationStarted() && !isSafeNetworkDelegated();

/**
 * Blocker decisions and notifications share {@link dailyQuotaRuleChecker} so
 * quota state transitions cannot drift. The 3-5 and 13-15 minute windows mirror
 * Android.
 */
export const notifyForDailyTimeQuotaReachedIfNeeded = async () => {
  const state = await dailyQuotaRuleChecker.check();
  const store = getTimeNotificationsStore();

  if (state.state === DailyQuotaRuleStateType.NO_LIMIT) {
    await resetAll(store);
    return;
  }

  if (state.state === DailyQuotaRuleStateType.QUOTA_EXCEEDED) {
    await displayDailyQuotaExceededIfNeeded(store);
    await resetFiveMinutesIfNotified(store);
    await resetFifteenMinutesIfNotified(store);
    return;
  }

  await handleTimeLeft(store, state.secondsLeft);
};

type TimeNotificationsStore = ReturnType<typeof getTimeNotificationsStore>;

const handleTimeLeft = async (
  store: TimeNotificationsStore,
  secondsLeft: number
) => {
  const minutesLeft = Math.floor(secondsLeft / ONE_MINUTE_IN_SECONDS);

  if (isInNotificationRange(minutesLeft, FIVE_MINUTES_NOTIFICATION_RANGE)) {
    await displayFiveMinutesIfNeeded(store);
    await resetDailyQuotaExceededIfNotified(store);
    await resetFifteenMinutesIfNotified(store);
    return;
  }

  if (isInNotificationRange(minutesLeft, FIFTEEN_MINUTES_NOTIFICATION_RANGE)) {
    await displayFifteenMinutesIfNeeded(store);
    await resetDailyQuotaExceededIfNotified(store);
    await resetFiveMinutesIfNotified(store);
    return;
  }

  await resetAll(store);
};

const isInNotificationRange = (
  minutesLeft: number,
  range: { min: number; max: number }
) => minutesLeft >= range.min && minutesLeft <= range.max;

const displayDailyQuotaExceededIfNeeded = async (
  store: TimeNotificationsStore
) => {
  if (store.isNotifiedDailyQuotaExceeded()) {
    return;
  }
  console.log("[timesUp] Time's up");
  display(TIME_QUOTA_NOTIFICATION_ID, t('Time check'), t("Time's up"));
  await store.setIsNotifiedDailyQuotaExceeded(true);
};

const displayFiveMinutesIfNeeded = async (store: TimeNotificationsStore) => {
  if (store.isNotified5MinutesForDailyQuota()) {
    return;
  }
  console.log('[timesUp] 5 minutes of screen time left today');
  display(
    TIME_QUOTA_NOTIFICATION_ID,
    t('Time check'),
    t('{{minutesLeft}} minutes of screen time left today', {
      minutesLeft: '5',
    })
  );
  await store.setIsNotified5MinutesForDailyQuota(true);
};

const displayFifteenMinutesIfNeeded = async (store: TimeNotificationsStore) => {
  if (store.isNotified15MinutesForDailyQuota()) {
    return;
  }
  console.log('[timesUp] 15 minutes of screen time left today');
  display(
    TIME_QUOTA_NOTIFICATION_ID,
    t('Time check'),
    t('{{minutesLeft}} minutes of screen time left today', {
      minutesLeft: '15',
    })
  );
  await store.setIsNotified15MinutesForDailyQuota(true);
};

const resetDailyQuotaExceededIfNotified = async (
  store: TimeNotificationsStore
) => {
  if (store.isNotifiedDailyQuotaExceeded()) {
    await store.setIsNotifiedDailyQuotaExceeded(false);
  }
};

const resetFiveMinutesIfNotified = async (store: TimeNotificationsStore) => {
  if (store.isNotified5MinutesForDailyQuota()) {
    await store.setIsNotified5MinutesForDailyQuota(false);
  }
};

const resetFifteenMinutesIfNotified = async (store: TimeNotificationsStore) => {
  if (store.isNotified15MinutesForDailyQuota()) {
    await store.setIsNotified15MinutesForDailyQuota(false);
  }
};

const resetAll = async (store: TimeNotificationsStore) => {
  await resetDailyQuotaExceededIfNotified(store);
  await resetFiveMinutesIfNotified(store);
  await resetFifteenMinutesIfNotified(store);
};
